/**
 * Klient mot vibeorg-battery-api.
 *
 * Retry-strategi: tunga endpoints (som /data) kan ta 20-30s eftersom de
 * hämtar 8760 timmar + spotpriser. Vid network failure (fetch throw TypeError)
 * gör vi 2 retries med exponential backoff. HTTP-status-fel retryas inte —
 * de är meningsfulla och ska bubblas till UI.
 */

import type { TibberHome, UserData } from "../types";

/**
 * API_URL detekteras dynamiskt så vi inte är beroende av att Railway
 * env-variabeln VITE_API_URL är korrekt satt:
 *   1. Hostname-detektering för kända produktions-domäner (vinner alltid)
 *   2. Explicit VITE_API_URL (dev/staging override)
 *   3. localhost-fallback
 *
 * Samma princip som getFrontendUrl i backend — frontend och backend
 * speglar varandra så de inte kan hamna i fel domän pga env-drift.
 * Hostname måste vinna över env eftersom env i praktiken kan vara satt
 * till en gammal URL från en tidigare deploy.
 */
export function getApiUrl(): string {
  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    if (host === "www.batterikalkyl.se" || host === "batterikalkyl.se") {
      return "https://api.batterikalkyl.se";
    }
    if (host === "vibeorg-battery-app-production.up.railway.app") {
      return "https://vibeorg-battery-api-production.up.railway.app";
    }
  }
  if (import.meta.env.VITE_API_URL) return import.meta.env.VITE_API_URL;
  return "http://localhost:3000";
}

const API_URL = getApiUrl();
const TIMEOUT_MS = 60_000;
const RETRY_DELAYS_MS = [1000, 3000]; // 2 retries, 4s totalt extra vid värsta fall

export interface SessionStatus {
  authenticated: boolean;
  expiresAt?: number;
  scope?: string;
  expired?: boolean;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public code: string,
    public status: number,
  ) {
    super(message);
  }
}

function isNetworkError(err: unknown): boolean {
  // fetch() throw:ar TypeError vid network failure (DNS, timeout, abort, etc).
  // HTTP-fel kommer från vår egen ApiError som ärver Error.
  return err instanceof TypeError;
}

async function fetchWithTimeout(url: string, init: RequestInit): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

async function get<T>(path: string, retries = RETRY_DELAYS_MS): Promise<T> {
  try {
    const response = await fetchWithTimeout(`${API_URL}${path}`, {
      credentials: "include",
    });
    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throw new ApiError(
        (errorBody as { details?: string }).details ??
          `Servern svarade ${response.status}`,
        (errorBody as { error?: string }).error ?? "unknown_error",
        response.status,
      );
    }
    return (await response.json()) as T;
  } catch (err) {
    // Retry bara på network-errors (TypeError från fetch). HTTP-fel bubblas direkt.
    if (isNetworkError(err) && retries.length > 0) {
      const [delay, ...rest] = retries;
      await new Promise((resolve) => setTimeout(resolve, delay));
      return get<T>(path, rest);
    }
    // Network error utan retries kvar — översätt till mer användbart meddelande
    if (isNetworkError(err)) {
      throw new ApiError(
        "Datahämtningen tog för lång tid. Försök igen om en stund.",
        "network_error",
        0,
      );
    }
    throw err;
  }
}

async function postNoBody<T>(path: string): Promise<T> {
  const response = await fetchWithTimeout(`${API_URL}${path}`, {
    method: "POST",
    credentials: "include",
  });
  if (!response.ok) {
    throw new ApiError(
      `Servern svarade ${response.status}`,
      "post_failed",
      response.status,
    );
  }
  return (await response.json()) as T;
}

export function getSession(): Promise<SessionStatus> {
  return get<SessionStatus>("/api/tibber/session");
}

export function getHomes(): Promise<{ homes: TibberHome[] }> {
  return get<{ homes: TibberHome[] }>("/api/tibber/homes");
}

/**
 * Hämta ett år (default) eller annan period av data från Tibber.
 * Returnerar färdig UserData som kan matas in i simulatorn.
 */
export function getData(homeId: string, hoursBack = 8760): Promise<UserData> {
  const params = new URLSearchParams({
    homeId,
    hoursBack: String(hoursBack),
  });
  return get<UserData>(`/api/tibber/data?${params.toString()}`);
}

export function logout(): Promise<{ ok: boolean }> {
  return postNoBody<{ ok: boolean }>("/api/tibber/logout");
}

export function startTibberAuth(returnTo?: string): void {
  const params = returnTo
    ? `?returnTo=${encodeURIComponent(returnTo)}`
    : "";
  window.location.href = `${API_URL}/api/tibber/auth/start${params}`;
}

/**
 * Berikar en CSV-uppladdad timserie med spotpriser från backend.
 * Datan skickas i klartext (ingen PII — frontend har redan strippat det
 * vid parsning). Backend matchar spotpriser per timme och returnerar
 * komplett HourPoint[]-array redo för simulering.
 */
export async function enrichCsvHours(
  priceArea: "SE1" | "SE2" | "SE3" | "SE4",
  hours: Array<{ from: string; importKWh: number; exportKWh: number }>,
): Promise<{
  hours: Array<{
    from: string;
    importKWh: number;
    exportKWh: number;
    spotPrice: number;
    quarterPrices?: number[];
  }>;
  metadata: {
    totalHours: number;
    hoursWithMissingPrice: number;
    firstTimestamp: string;
    lastTimestamp: string;
  };
}> {
  const response = await fetchWithTimeout(`${API_URL}/api/data/enrich-spot`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ priceArea, hours }),
  });
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new ApiError(
      text || `HTTP ${response.status}`,
      "csv_enrich_failed",
      response.status,
    );
  }
  return response.json();
}
